'use client';

import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';

import { ProfessionalPrivateProfile, getProfessionalPrivateProfile } from '@website/services/professionals.service';

import { useCurrentUser } from './useCurrentUser';

/**
 * Query key factory so callers (e.g. the verification page) can invalidate
 * the current user's professional profile after a mutation without having to
 * duplicate the locale/id tuple.
 */
export function currentProfessionalQueryKey(userId: string | undefined, locale: string) {
  return ['professional', 'private', userId ?? 'anonymous', locale] as const;
}

/**
 * Fetches the private professional profile for the currently-authenticated
 * user. Returns `null` when the user is not a professional (or not logged in).
 *
 * The private profile exposes the moderation `status`
 * (`pending` | `approved` | `rejected`) and the revisions history, both of
 * which drive the verification banner and the ID verification page.
 */
export function useCurrentProfessional() {
  const locale = useLocale();
  const { data: user } = useCurrentUser();

  const enabled = !!user?.id && !!user.isProfessional;

  const query = useQuery<ProfessionalPrivateProfile | null>({
    queryKey: currentProfessionalQueryKey(user?.id, locale),
    queryFn: async () => {
      if (!user?.id) return null;
      return getProfessionalPrivateProfile(user.id, locale);
    },
    enabled,
    staleTime: 30 * 1000
  });

  return {
    ...query,
    professional: query.data ?? null,
    isProfessional: !!user?.isProfessional
  };
}
